import * as React from 'react';
import { Component, Fragment } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { computed, isArrayLike } from 'mobx';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import Icon from './Icon';
import DownloadControls from './Document/DownloadControls';
import Translate from './Translate';
import WatchListToggle from './WatchListToggle';
import ChangeIndicator from './ChangeIndicator';
import DocumentMenu from './Document/DocumentMenu';
import Tooltip from './Tooltip';
import { ShareclassCardList, ShareclassCardRow } from './ShareclassCards';
import StarRating from './StarRating';
import DynamicLinkProps from '../models/components/DynamicLinkProps';
import DynamicLink from './DynamicLink';
import {
    AccessibilityStore,
    DataStore,
    EntityGroupStore,
    EntityStore,
    HistoryStore,
    SuperscriptScope,
} from '../stores';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import {
    DownloadControlActions,
    PreviewActionOptions,
} from '../models/configuration/explorer/DownloadControls';
import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';
import { Group } from '../models/configuration/explorer/Group';
import { Theme } from '../models/Theme';
import Dropdown from './Dropdown';
import InlineDisclaimers from './InlineDisclaimers';
import { ColumnDocumentsGrid } from '../models/configuration/documents/ColumnDocumentsGrid';
import { isNullOrEmpty } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import VisuallyHiddenWrapper from './VisuallyHiddenWrapper';
import { generateGuid } from '@kurtosys/ksys-app-template/dist/utils/guid';
import { DataLocator } from '../models/configuration/DataLocator';
import { ConditionalDisclaimer } from 'src/models/configuration/disclaimers/ConditionalDisclaimer';

const ShareclassCardLabel = styled<any, any>(
    'div',
    'shareclassCardLabel',
    'shareclassCardLabelValue'
)`
    ${propStyles};
`;

ShareclassCardLabel.defaultProps = {
    fontFamily: 'heading',
    fontWeight: 'medium',
};

const ShareclassCardValue = styled<any, any>(
    'div',
    'shareclassCardValue',
    'shareclassCardLabelValue'
)`
    ${propStyles};
`;

ShareclassCardValue.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'body',
    fontWeight: 'regular',
};

const starIconStyleProps = {
    height: '1.2em',
    width: '1.2em',
};

const SeparatorWrapper = styled<any, any>(
    'span',
    'separatorWrapper',
    'shareclassCardLabelValue'
)`
    ${propStyles};
    white-space: pre;
`;

const ShareclassCardDownloadContent = styled<any, any>(
    'div',
    'shareclassCardDownloadContent',
    'shareclassCardLabelValue'
)`
    ${propStyles};
`;

const ShareclassCardDocumentContent = styled<any, any>(
    'div',
    'shareclassCardDocumentContent',
    'shareclassCardLabelValue'
)`
    ${propStyles};
`;

interface ShareclassCardLabelValueProps {
    theme?: Theme;
    /** Injected */
    dataStore?: DataStore;
    historyStore?: HistoryStore;
    entityStore?: EntityStore;
    entityGroupStore?: EntityGroupStore;
    rowAccessibleContext: string;
    accessibilityStore?: AccessibilityStore;
    /**  */
    column: ColumnConfig;
    columnIndex: number;
}

// Adds the mutated properties so that we can track what is not included in the original interface
interface WorkingDataLocator extends DataLocator {
    value: any;
    performanceCheck: any;
}

// Adds the mutated properties so that we can track what is not included in the original interface
interface WorkingColumnConfig extends ColumnConfig {
    multiple?: boolean;
    url?: string;
    cellDisclaimers?: Array<any>;
    values?: Array<any>;
    disclaimers?: any;
    options?: any;
    columnIndex: number;
    value?: any;
}

// Adds the mutated properties so that we can track what is not included in the original interface
interface WorkingGroupConfig extends Group {
    multiple?: boolean;
    url?: string;
    cellDisclaimers?: Array<any>;
    values?: Array<any>;
}

// Used for passing props between different column types
interface WorkingOptions {
    columnConfig: WorkingColumnConfig;
    labelId: string;
    props?: {
        [key: string]: any;
    };
    themeKey?: string;
    /**
     * The context for the card which is determined by the `isRowIdentifier`, this is then used for the `aria-labelledby` and `aria-describedby`.
     */
    cardContextId?: string;
}

@inject(
    'dataStore',
    'historyStore',
    'entityStore',
    'entityGroupStore',
    'accessibilityStore'
)
@observer
class ShareclassCardLabelValue extends Component<
    ShareclassCardLabelValueProps
> {
    // This is to store the column value without any html elements (replacementValue) for use of the data layer logging
    rawValue: string = '';
    constructor(props) {
        super(props);

        if (this.props.column.groups) {
            this.columnStore.setInitialGroupIndex(
                this.props.columnIndex,
                this.props.column
            );
        }
    }

    @computed
    get columnStore() {
        return this.props.dataStore.columnStore;
    }

    @computed
    get selectedGroupIndex() {
        return this.columnStore.getSelectedGroupIndex(
            this.props.columnIndex,
            this.props.column.groups
        );
    }

    @computed
    get column() {
        return this.props.column.groups
            ? (this.props.column.groups[
                  this.selectedGroupIndex
              ] as WorkingGroupConfig)
            : (this.props.column as WorkingColumnConfig);
    }

    @computed
    get downloadControlActions(): DownloadControlActions {
        const { dataStore } = this.props;
        return dataStore?.config.getDownloadControlActionsOrDefault(
            this.props.column?.actions
        );
    }

    @computed
    get dropdownTheme() {
        const { theme } = this.props;
        const result = {
            ...theme,
            ColumnDropdownWrapper: {
                flex: '1 0 auto',
                ...theme.ColumnDropdownWrapper,
                ...theme.ShareclassCardRowDropdownWrapper,
            },
            ColumnDropdownInnerWrapper: {
                position: 'relative',
                ...theme.ColumnDropdownInnerWrapper,
                ...theme.ShareclassCardRowDropdownInnerWrapper,
            },
            ColumnDropdownButton: {
                fontFamily: 'heading',
                fontWeight: 'normal',
                margin: '0 0 0.5em 0',
                padding: '0 0 0.5em 0',
                borderWidth: '1px',
                borderStyle: 'none none solid none',
                borderColor: 'gray500',
                borderRadius: '0px',
                justifyContent: 'center',
                ...theme.ColumnGroupLabel,
                ...theme.ColumnDropdownGroupLabel,
            },
            ColumnDropdownButtonLabel: {
                ...theme.ColumnDropdownGroupLabel,
                ...theme.ShareclassCardRowDropdownGroupLabel,
            },
            ColumnDropdownChevron: {
                display: 'inline-block',
                verticalAlign: 'middle',
                height: '1em',
                width: '1em',
                margin: '0 0.5em',
                ...theme.ColumnDropdownChevron,
                ...theme.ShareclassCardRowDropdownChevron,
            },
            ColumnDropdownMenu: {
                position: 'absolute',
                top: 'calc(100% + 1px)',
                zIndex: 'dropdown',
                width: ['100%', 'auto'],
                margin: ['0', '0 10%'],
                bgColor: 'white',
                borderWidth: '1px',
                borderColor: 'gray300',
                borderStyle: 'solid',
                borderRadius: 'sm',
                boxShadow: 'sm',
                ...theme.ColumnDropdownMenu,
                ...theme.ShareclassCardRowDropdownMenu,
            },
            ColumnDropdownList: {
                maxHeight: '300px',
                overflowX: 'hidden',
                overflowY: 'auto',
                textAlign: 'center',
                ...theme.ColumnDropdownList,
                ...theme.ShareclassCardRowDropdownList,
            },
            ColumnDropdownListItem: {
                fontFamily: 'body',
                fontWeight: 'normal',
                paddingY: 'sm',
                paddingX: 'md',
                borderWidth: '1px',
                borderStyle: 'none none solid none',
                borderColor: 'gray300',
                fontSize: 'sm',
                cursor: 'pointer',
                alternated: {
                    bgColor: 'gray100',
                },
                hover: {
                    bgColor: 'gray100',
                },
                selected: {
                    color: 'primary',
                    fontWeight: 'bold',
                },
                ...theme.ColumnDropdownListItem,
                ...theme.ShareclassCardRowDropdownListItem,
            },
            ColumnDropdownDisclaimerWrapper: {
                margin: '0 0 0.5em 0',
                padding: '0 0 0.5em 0',
                ...theme.ColumnDropdownDisclaimerWrapper,
            },
        };
        return result;
    }

    getGroupOrDropdownContent = ({
        columnConfig,
        props: { dynamicLinkProps },
        labelId,
    }: WorkingOptions) => {
        const { theme, dataStore, rowAccessibleContext } = this.props;
        const { columnStore } = dataStore;
        const { selectedGroupIndex } = this;
        let mappedColumnOptions;

        if (columnConfig.type === 'dropdown') {
            mappedColumnOptions = columnConfig.options.map(option => {
                return {
                    label: option.label,
                    columnIndex: columnConfig.columnIndex,
                    id: option.groupIndex,
                    disclaimers: (columnConfig as any).mappedDisclaimers,
                };
            });
        }

        return (
            <Fragment>
                {columnConfig.type === 'group' ? (
                    <ShareclassCardLabel {...theme.ShareclassCardGroupLabel}>
                        <Translate>{columnConfig.label}</Translate>
                        <Tooltip
                            content={columnConfig.disclaimers}
                            accessibilityContext={{
                                key: `More information about the {label}`,
                                placeholders: {
                                    label: columnConfig.label,
                                },
                            }}
                            analyticsContext={{
                                metricContext: `shareclassCard-${camelCase(
                                    columnConfig.label
                                )}`,
                                source: 'ShareclassCardLabel',
                                tooltipCode: columnConfig.disclaimerTypes,
                            }}
                        />
                    </ShareclassCardLabel>
                ) : (
                    <Dropdown
                        items={mappedColumnOptions}
                        selectedItem={mappedColumnOptions.find(option => {
                            return option.id === selectedGroupIndex;
                        })}
                        selectItemFn={item => {
                            columnStore.setSelectedGroup(
                                item.columnIndex as any,
                                item.id
                            );
                        }}
                        chevronIcon={'ColumnDropdownChevronIcon.svg'}
                        chevronIconExpanded={
                            'ColumnDropdownChevronIconExpanded.svg'
                        }
                        themeKeys={{
                            root: 'ColumnDropdownWrapper',
                            button: 'ColumnDropdownButton',
                            buttonIcon: 'ColumnDropdownChevron',
                            buttonLabel: 'ColumnDropdownButtonLabel',
                            listWrapper: 'ColumnDropdownMenu',
                            list: 'ColumnDropdownList',
                            listItem: 'ColumnDropdownListItem',
                            wrapper: 'ColumnDropdownInnerWrapper',
                            disclaimerWrapper:
                                'ColumnDropdownDisclaimerWrapper',
                        }}
                        theme={this.dropdownTheme}
                        buttonAccessibilityKey={'ColumnDropdownButton'}
                    />
                )}
                <ShareclassCardList
                    aria-labelledby={labelId}
                    flex="1 0 100%"
                    paddingX="0"
                >
                    {columnConfig.values &&
                        columnConfig.values.map((c, cIndex) => {
                            const itemLabelId = `${camelCase(
                                c?.label || 'groupLabel'
                            )}-${generateGuid()}`;
                            let itemLabelComponent = (
                                <ShareclassCardLabel
                                    id={itemLabelId}
                                    paddingX="md"
                                    {...theme.ShareclassCardLabel}
                                    {...theme.ShareclassCardLabelNested}
                                >
                                    <Translate>{c.label}</Translate>
                                    <Tooltip
                                        content={c.disclaimers}
                                        accessibilityContext={{
                                            key: `More information about the {label}`,
                                            placeholders: {
                                                label: c.label,
                                            },
                                        }}
                                        rowAccessibleContext={
                                            rowAccessibleContext
                                        }
                                        analyticsContext={{
                                            metricContext: `shareclassCard-${camelCase(
                                                c.label
                                            )}`,
                                            source: 'ShareclassCardLabel',
                                            tooltipCode: c.disclaimerTypes,
                                        }}
                                    />
                                </ShareclassCardLabel>
                            );
                            if (c.showLabel === false) {
                                itemLabelComponent = (
                                    <VisuallyHiddenWrapper>
                                        {itemLabelComponent}
                                    </VisuallyHiddenWrapper>
                                );
                            }
                            return (
                                <ShareclassCardRow
                                    key={`${c.label}-${cIndex}`}
                                    paddingY="sm"
                                >
                                    <Fragment>
                                        {itemLabelComponent}
                                        {this.getShareclassCardValue({
                                            columnConfig: c,
                                            props: {
                                                dynamicLinkProps,
                                                shareclassCardValueProps: {
                                                    ...theme.ShareclassCardValueNested,
                                                },
                                            },
                                            labelId: itemLabelId,
                                        })}
                                    </Fragment>
                                </ShareclassCardRow>
                            );
                        })}
                </ShareclassCardList>
            </Fragment>
        );
    };
    getDownloadContent = ({ columnConfig, labelId }: WorkingOptions) => {
        const {
            theme,
            dataStore,
            entityStore,
            children,
            rowAccessibleContext,
        } = this.props;
        const {
            value: shareClass,
            shareClassDownloads,
            expanded,
        } = entityStore;
        const content = (
            <Fragment>
                <DownloadControls
                    aria-labelledby={labelId}
                    count={entityStore.downloadCount}
                    componentThemeKey="ShareclassCard"
                    hidePreview={dataStore.isZipDocument(shareClassDownloads)}
                    onClick={e => {
                        e.stopPropagation();
                        dataStore.downloadDocuments(
                            shareClassDownloads,
                            shareClass
                        );
                    }}
                    href={dataStore.getDocumentLink(
                        shareClassDownloads,
                        shareClass
                    )}
                    onPreviewClick={(
                        e,
                        elementOptions: PreviewActionOptions
                    ) => {
                        e.stopPropagation();
                        dataStore.previewDocuments(
                            shareClassDownloads,
                            shareClass,
                            elementOptions
                        );
                    }}
                    showLabel={false}
                    label={dataStore.getFileName(shareClassDownloads)}
                    fileName={dataStore.getFileName(
                        shareClassDownloads,
                        shareClass
                    )}
                    metricContext={`shareclassCard-${camelCase(
                        columnConfig.label
                    )}-${camelCase(shareClass.clientCode)}`}
                    superscriptScope={SuperscriptScope.module}
                    document={shareClassDownloads}
                    documentDisplayName={camelCase(shareClass.clientCode)}
                    actions={this.downloadControlActions}
                    disableExtendedInfo={columnConfig.disableExtendedInfo}
                    describedBy={rowAccessibleContext}
                />
                <Icon
                    fileName={
                        expanded
                            ? 'DocumentDropdownIconExpanded'
                            : 'DocumentDropdownIcon'
                    }
                    height="1.4em"
                    width="1.4em"
                    {...theme.ShareclassCardDownloadIcon}
                />
            </Fragment>
        );
        return (
            <Fragment>
                {this.getShareclassCardLabel({ columnConfig, labelId })}
                <ShareclassCardDownloadContent
                    {...theme.ShareclassCardDownloadContent}
                >
                    <ShareclassCardValue>
                        <InlineDisclaimers
                            accessibilityContext={{
                                key: `More information about the {label}`,
                                placeholders: {
                                    label: content,
                                },
                            }}
                            conditionalDisclaimers={
                                columnConfig.cellDisclaimers
                            }
                            superscriptScope={SuperscriptScope.module}
                            analyticsContext={{
                                source: 'shareclassCardValue',
                                sourceContext: content,
                            }}
                        >
                            {content}
                        </InlineDisclaimers>
                    </ShareclassCardValue>

                    {expanded && children}
                </ShareclassCardDownloadContent>
            </Fragment>
        );
    };
    getWatchlistContent = ({ columnConfig, labelId }: WorkingOptions) => {
        const { theme, entityStore, rowAccessibleContext } = this.props;
        return (
            <Fragment>
                {this.getShareclassCardLabel({ columnConfig, labelId })}
                <WatchListToggle
                    onClick={e => {
                        e.stopPropagation();
                        entityStore.toggleWatchList();
                    }}
                    active={entityStore.inWatchList}
                    fontSize="1em"
                    describedby={rowAccessibleContext}
                    metricContext={camelCase(entityStore.value.clientCode)}
                    // override the WatchListToggle theme for mobile styling
                    theme={{
                        WatchListToggle:
                            theme.ShareclassCardWatchListToggle ||
                            theme.WatchListToggle,
                    }}
                />
            </Fragment>
        );
    };

    getColumnDocumentsGridConfig(column): ColumnDocumentsGrid | undefined {
        const { columnDocumentsGrid } = column;
        // If the column does not have the documents grid config configured,
        // then return undefined, the getDocumentColumnContent will handle this as normal
        if (isNullOrEmpty(columnDocumentsGrid)) {
            return undefined;
        }
        // If the column config has the documents grid configuration then return as is
        // to override the module level configuration
        if (columnDocumentsGrid.documentsGrid) {
            return column.columnDocumentsGrid;
        }
        // Else add the documents grid config that is defined on the module level
        const { dataStore } = this.props;
        if (dataStore) {
            const { documentsGrid } = dataStore.config.selectedModuleConfig;
            if (documentsGrid) {
                return {
                    ...columnDocumentsGrid,
                    documentsGrid,
                };
            }
        }
    }

    getDocumentContent = ({ columnConfig, labelId }: WorkingOptions) => {
        const {
            theme,
            dataStore,
            entityStore,
            entityGroupStore,
            rowAccessibleContext,
        } = this.props;
        const shareClassLabel = this.getShareclassCardLabel({
            columnConfig,
            labelId,
        });
        const isDocumentsGrid = Boolean(
            this.getColumnDocumentsGridConfig(columnConfig)
        );

        return (
            <Fragment>
                {Array.isArray(columnConfig.value) &&
                (columnConfig.value.length > 0 || isDocumentsGrid) ? (
                    <Fragment>
                        <InlineDisclaimers
                            accessibilityContext={{
                                key: `More information about the {label} documents`,
                                placeholders: {
                                    label: columnConfig.label,
                                },
                            }}
                            conditionalDisclaimers={
                                columnConfig.cellDisclaimers
                            }
                            superscriptScope={SuperscriptScope.module}
                            analyticsContext={{
                                source: 'shareclassCardLabel',
                                sourceContext: shareClassLabel,
                            }}
                        >
                            {shareClassLabel}
                        </InlineDisclaimers>
                        <ShareclassCardDocumentContent
                            {...theme.ShareclassCardDocumentContent}
                        >
                            <DocumentMenu
                                aria-labelledby={labelId}
                                superscriptScope={SuperscriptScope.module}
                                documents={columnConfig.value}
                                selected={entityStore.documentSelected}
                                onChange={entityGroupStore.toggleDownload}
                                showSingleDocument={
                                    columnConfig.showSingleDocument
                                }
                                showSingleDocumentLabel={
                                    columnConfig.showSingleDocumentLabel
                                }
                                showCount={isDocumentsGrid}
                                showCheckbox={isDocumentsGrid}
                                displayOption={'inline'}
                                theme={{
                                    ...theme,
                                    ...{
                                        DocumentDropdownButton: {
                                            justifyContent: 'flex-end',
                                        },
                                        DownloadButtonIconNoLabel: {
                                            margin: '0 0 0 auto',
                                        },
                                    },
                                }}
                                metricContext={`shareclassCard-${camelCase(
                                    columnConfig.label
                                )}-${camelCase(entityStore.value.clientCode)}`}
                                disableExtendedInfo={
                                    columnConfig.disableExtendedInfo
                                }
                                columnDocumentsGrid={this.getColumnDocumentsGridConfig(
                                    columnConfig
                                )}
                                describedBy={rowAccessibleContext}
                            />
                        </ShareclassCardDocumentContent>
                    </Fragment>
                ) : (
                    <Fragment>
                        {shareClassLabel}
                        <ShareclassCardValue
                            aria-labelledby={labelId}
                            {...theme.ShareclassCardValue}
                        >
                            <Translate>
                                {columnConfig.noDataPlaceholder ||
                                    dataStore.config.noDataPlaceholder}
                            </Translate>
                        </ShareclassCardValue>
                    </Fragment>
                )}
            </Fragment>
        );
    };
    getPricesContent = ({ columnConfig, labelId }: WorkingOptions) => {
        const { historyStore, entityStore, rowAccessibleContext } = this.props;
        const dataLocator = columnConfig.data as WorkingDataLocator;
        return (
            <Fragment>
                {this.getShareclassCardLabel({ columnConfig, labelId })}
                {dataLocator.performanceCheck && !entityStore.hasPerformance ? (
                    <Translate>{dataLocator.value}</Translate>
                ) : (
                    <DownloadControls
                        aria-labelledby={labelId}
                        display="inline-flex"
                        onClick={() => {
                            historyStore.getPriceHistory(columnConfig, null);
                        }}
                        hidePreview={true}
                        count={1}
                        showCount={false}
                        // Due to historic prices building up filename on the response, we can not accurately set this. Default to historic_prices.csv
                        fileName={'historic_prices.csv'}
                        metricContext={`shareclassCard-${camelCase(
                            columnConfig.label
                        )}-${camelCase(entityStore.value.clientCode)}`}
                        superscriptScope={SuperscriptScope.module}
                        disableExtendedInfo={columnConfig.disableExtendedInfo}
                        actions={this.downloadControlActions}
                        describedBy={rowAccessibleContext}
                    />
                )}
            </Fragment>
        );
    };
    getStarsContent = ({ columnConfig, labelId }: WorkingOptions) => {
        const { theme, entityStore } = this.props;
        const dataLocator = columnConfig.data as WorkingDataLocator;
        if (dataLocator.performanceCheck && !entityStore.hasPerformance) {
            return <Translate>{dataLocator.value}</Translate>;
        }
        return (
            <Fragment>
                {this.getShareclassCardLabel({ columnConfig, labelId })}
                <InlineDisclaimers
                    accessibilityContext={{
                        key: `More information about the {label} stars rating`,
                        placeholders: {
                            label: columnConfig.label,
                        },
                    }}
                    conditionalDisclaimers={columnConfig.cellDisclaimers}
                    superscriptScope={SuperscriptScope.module}
                    analyticsContext={{
                        source: 'shareclassCardLabel',
                        sourceContext: dataLocator.value,
                    }}
                >
                    <StarRating
                        aria-labelledby={labelId}
                        rating={dataLocator.value}
                        noDataPlaceholder={columnConfig.noDataPlaceholder}
                        showEmptyStars={columnConfig.type === 'starsOutOf'}
                        display="block"
                        theme={{
                            ...theme,
                            ...theme.StarRatingColumnWrapper,
                            StarIcon: {
                                ...starIconStyleProps,
                                ...theme.StarIconColumn,
                            },
                            StarOutlineIcon: {
                                ...starIconStyleProps,
                                ...theme.StarOutlineIconColumn,
                            },
                        }}
                    />
                </InlineDisclaimers>
            </Fragment>
        );
    };
    getSimpleContent = ({
        columnConfig,
        labelId,
        props: { dynamicLinkProps },
    }: WorkingOptions) => {
        const columnLabelId = `${labelId}-label`;
        return (
            <Fragment>
                {this.getShareclassCardLabel({
                    columnConfig,
                    labelId: columnLabelId,
                })}
                {this.getShareclassCardValue({
                    columnConfig,
                    props: { dynamicLinkProps },
                    labelId: columnLabelId,
                    cardContextId: labelId,
                })}
            </Fragment>
        );
    };
    getShareclassCardLabel = ({
        columnConfig,
        props: { labelProps } = { labelProps: {} },
        themeKey = 'ShareclassCardLabel',
        labelId,
    }: WorkingOptions) => {
        const { theme } = this.props;
        const columnLabelId = `${labelId}-${columnConfig.label}`;

        let disclaimers: Array<ConditionalDisclaimer> = [];
        const config = this.props.dataStore.config;
        if (
            config &&
            config.selectedModule &&
            config.selectedModule.disclaimers
        ) {
            disclaimers = config.selectedModule.disclaimers;
        }

        let conditionalDisclaimers;
        // The Shareclass Card Label is the column heading and uses disclaimerTypes to determine what to display
        // Cell Disclaimers are only used in the Shareclass Card Value
        if (columnConfig.disclaimerTypes) {
            conditionalDisclaimers =
                columnConfig.disclaimerTypes &&
                columnConfig.disclaimerTypes.map(type => {
                    const display =
                        disclaimers.find(
                            ({ disclaimerType }) => disclaimerType === type
                        )?.display || undefined;
                    return {
                        display,
                        disclaimerType: type,
                    };
                });
        }
        const columnLabel = (
            <Translate id={columnLabelId}>{columnConfig.label}</Translate>
        );
        if (columnConfig.showLabel === false) {
            return <VisuallyHiddenWrapper>{columnLabel}</VisuallyHiddenWrapper>;
        }
        return (
            <ShareclassCardLabel {...labelProps} {...theme[themeKey]}>
                <InlineDisclaimers
                    accessibilityContext={{
                        key: `More information about the {label}`,
                        placeholders: {
                            label: columnLabel,
                        },
                    }}
                    conditionalDisclaimers={conditionalDisclaimers}
                    superscriptScope={SuperscriptScope.module}
                    analyticsContext={{
                        source: 'shareclassCardLabel',
                        sourceContext: columnConfig.label,
                    }}
                >
                    {columnLabel}
                </InlineDisclaimers>
            </ShareclassCardLabel>
        );
    };
    getShareclassCardValue = ({
        columnConfig,
        props: { dynamicLinkProps, shareclassCardValueProps = {} },
        themeKey = 'ShareclassCardValue',
        labelId,
        cardContextId,
    }: WorkingOptions) => {
        const { theme, dataStore, accessibilityStore } = this.props;
        const dataRecords = isArrayLike(columnConfig.data)
            ? columnConfig.data
            : [columnConfig.data];

        const values = [];
        let replacementValue = '';
        dataRecords.forEach((dataRecord, index) => {
            replacementValue += `${(dataRecord as WorkingDataLocator).value}${
                columnConfig.multiple && dataRecord.separator
                    ? dataRecord.separator
                    : ''
            }`;
            let inner = (
                <Fragment>
                    <Translate>
                        {(dataRecord as WorkingDataLocator).value}
                    </Translate>
                    {columnConfig.multiple && dataRecord.separator && (
                        <SeparatorWrapper>
                            {dataRecord.separator}
                        </SeparatorWrapper>
                    )}
                </Fragment>
            );
            if (dataRecord.showChange) {
                inner = (
                    <ChangeIndicator
                        dataStore={dataStore}
                        showChange={dataRecord.showChange}
                        value={(dataRecord as WorkingDataLocator).value}
                        separator={dataRecord.separator}
                    />
                );
            }
            values.push(
                <Fragment key={`${dataRecord.code}-${index}`}>{inner}</Fragment>
            );
        });
        this.rawValue = replacementValue;
        if (dynamicLinkProps.showLink) {
            dynamicLinkProps.ariaLabelReplacements = {
                label: columnConfig.label,
                value: replacementValue,
            };
            dynamicLinkProps.ariaLabelKey = 'shareClassRowLinks';
        }

        return (
            <ShareclassCardValue
                aria-describedby={labelId}
                {...theme[themeKey]}
                {...shareclassCardValueProps}
            >
                <InlineDisclaimers
                    accessibilityContext={{
                        key: `More information about the {label}`,
                        placeholders: {
                            label: accessibilityStore.linkAccessibilityValue(
                                'shareClassRowLinks',
                                {
                                    label: columnConfig.label,
                                    value: replacementValue,
                                }
                            ),
                        },
                    }}
                    conditionalDisclaimers={columnConfig.cellDisclaimers}
                    superscriptScope={SuperscriptScope.module}
                    analyticsContext={{
                        source: 'shareclassCardLabel',
                        sourceContext: values,
                    }}
                >
                    <DynamicLink {...dynamicLinkProps}>
                        <span
                            id={
                                columnConfig.isRowIdentifier
                                    ? cardContextId
                                    : undefined
                            }
                        >
                            {values}
                        </span>
                    </DynamicLink>
                </InlineDisclaimers>
            </ShareclassCardValue>
        );
    };
    onClick = (link: string) => {
        const { dataStore } = this.props;
        dataStore.logDataLayer(
            'entity_data_card',
            {
                [this.column.label]: this.rawValue,
                url: link,
            },
            'click'
        );
    };
    getContent = column => {
        const {
            theme,
            dataStore,
            entityStore,
            rowAccessibleContext,
        } = this.props;
        const dynamicLinkProps: DynamicLinkProps = {
            dataStore,
            queryContext: entityStore.queryContext,
            config: column,
            showLink: false,
            theme: theme.ShareclassColumnLink,
            metricContext: `shareclassCard-${camelCase(
                column.label
            )}-${camelCase(entityStore.value.clientCode)}`,
            onClick: this.onClick,
        };
        const columnOptions: WorkingOptions = {
            labelId: rowAccessibleContext,
            columnConfig: column,
        };
        let content;
        if (['group', 'dropdown'].includes(column.type)) {
            dynamicLinkProps.showLink = true;
            content = this.getGroupOrDropdownContent({
                ...columnOptions,
                props: { dynamicLinkProps },
            });
        } else if (column.type === 'download') {
            content = this.getDownloadContent(columnOptions);
        } else if (column.type === 'watchlist' && dataStore.watchListEnabled) {
            content = this.getWatchlistContent(columnOptions);
        } else if (column.type === 'document') {
            content = this.getDocumentContent(columnOptions);
        } else if (column.type === 'prices') {
            content = this.getPricesContent(columnOptions);
        } else if (column.type === 'stars' || column.type === 'starsOutOf') {
            content = this.getStarsContent(columnOptions);
        } else {
            dynamicLinkProps.showLink = true;
            content = this.getSimpleContent({
                ...columnOptions,
                props: { dynamicLinkProps },
            });
        }

        return content;
    };

    render() {
        return this.getContent(this.column);
    }
}

export default withTheme(ShareclassCardLabelValue) as any;



// WEBPACK FOOTER //
// ./src/Components/ShareclassCardLabelValue.tsx